import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Linking, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { Button, HStack, theme, useResponsive } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

// ────────────────────────────────────────────────────────────────────────────────

export const OrderStatusCtas = (props: OrderStatusCtasProps) => {
  const {
    orderId = '',
    orderTotal = 0,
    isPendingFeedback,
    momoSurveyUrl,
    canReorder,
    onReorder,
    onRateOrder,
  } = props;

  const { formatMessage } = useIntl();
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const { match } = useResponsive();
  const { track } = useTelemetry();

  const orderTotalPrice = formatPrice(orderTotal);

  // ─── Helpers ─────────────────────────────────────────────────────

  const handleRateOrder = useCallback(() => {
    if (!orderId) return;

    if (momoSurveyUrl) {
      track('order_status.in_store_rate_order_cta.press');
      Linking.openURL(momoSurveyUrl);
      return;
    }

    onRateOrder?.();
  }, [orderId, track, momoSurveyUrl, onRateOrder]);

  // ─── Layout ──────────────────────────────────────────────────────

  const stackStyle = match([undefined, styles.stackSM]);
  const hasTwoButtons = isPendingFeedback && canReorder;
  const buttonsStackItemsPerRow = match([hasTwoButtons ? 2 : 1, undefined]);
  const buttonsSize = match(['large', 'medium'] as const);

  // ─── Styles ──────────────────────────────────────────────────────

  const containerStyle = useStyle(() => ({ paddingBottom: safeAreaBottom }), [safeAreaBottom]);

  // ─────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyle}>
      <HStack itemsPerRow={buttonsStackItemsPerRow} gap={theme.spacing['4']} style={stackStyle}>
        {isPendingFeedback ? (
          <Button
            palette="secondary"
            size={buttonsSize}
            accessibilityLabel={formatMessage(messages.rateOrder)}
            accessibilityHint={formatMessage(messages.rateOrder)}
            onPress={handleRateOrder}
          >
            {formatMessage(messages.rateOrder)}
          </Button>
        ) : null}

        {canReorder ? (
          <Button
            palette="primary"
            size={buttonsSize}
            accessibilityLabel={formatMessage(messages.reorder)}
            accessibilityHint={formatMessage(messages.reorder)}
            onPress={onReorder}
          >
            {isPendingFeedback
              ? formatMessage(messages.reorder)
              : formatMessage(messages.reorderTotal, {
                  total: orderTotalPrice,
                })}
          </Button>
        ) : null}
      </HStack>
    </View>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  stackSM: {
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────────

const formatPrice = (amount: number): string => {
  return `$${(amount / 100).toFixed(2)}`;
};

// ─── Types ──────────────────────────────────────────────────────────────────────

export type OrderStatusCtasProps = {
  orderId?: string;
  orderTotal?: number;
  isPendingFeedback: boolean;
  momoSurveyUrl?: string | null;
  canReorder: boolean;
  onReorder: () => void;
  onRateOrder?: () => void;
};

// ─── Messages ────────────────────────────────────────────────────────────────────

const messages = defineMessages({
  rateOrder: {
    defaultMessage: 'Rate order',
    description: 'Order Status | Bottom CTAs | Rate order button',
  },
  reorder: {
    defaultMessage: 'Reorder',
    description: 'Order Status | Bottom CTAs | Reorder button',
  },
  reorderTotal: {
    defaultMessage: 'Reorder | {total}',
    description: 'Order Status | Bottom CTAs | Reorder with total button',
  },
});
